import React from 'react';
import { displayActionCardHeader } from 'modules/automation/utils/displayActionCardHeader';
import { Controller, useFormContext } from 'react-hook-form';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';

import LabelName from 'components/UI/LabelName/LabelName';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import AttachmentInput from '../../AttachmentInput';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { assignToItemTemplate } from '../../AssignToItemTemplate';
import { formatUserDropdown } from 'modules/automation/utils/formatUserDropdown';
import { automationModules } from 'constants/automation';
import useLawftIdList from 'hooks/useLawftIdList';

export default function SendEmail({ index }) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();
  const { lawftIds } = useLawftIdList();

  const lawftIdsOption = lawftIds.map((lawftId) => ({
    label: lawftId?.lawft_id_name,
    value: lawftId?.lawft_id,
  }));

  const trigger = watch('trigger') || {};
  const isForLeadAutomation =
    watch('is_lead_automation') || (trigger?.module === automationModules.LEADS && trigger?.code !== 'TRIG_LEAD_CONV_CASE');

  // Selected values for cross-disabling between To and Cc
  const toSelected = watch(`actions[${index}].recipient_list`) || [];
  const ccSelected = watch(`actions[${index}].cc_list`) || [];

  const toSelectedKeys = new Set(toSelected || []);
  const ccSelectedKeys = new Set(ccSelected || []);

  return (
    <>
      {displayActionCardHeader('Compose Email')}
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <LabelName required={true} htmlFor="message_sender_assignee">
            Sender
          </LabelName>
          <Controller
            name={`actions[${index}].message_sender_assignee`}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field: { name, value, onChange, ref } }) => (
              <Dropdown
                name={name}
                value={value ?? []}
                options={
                  formatUserDropdown({
                    users: [],
                    hideInactive: false,
                    includeContacts: false,
                    includesUserGroups: false,
                    includesTeamMembers: true,
                    includePrimarySecondary: false,
                    includesFirmUsers: false,
                    isLead: isForLeadAutomation,
                  }) || []
                }
                placeholder="Please Select Sender"
                className="w-100 p-dropdown p-component p-inputwrapper"
                filter
                onChange={(e) => onChange(e.value)}
                optionValue="value"
                optionLabel="label"
                inputRef={ref}
                optionGroupLabel="groupLabel"
                optionGroupChildren="items"
              />
            )}
          />
          <RequiredValidation errors={errors} field={`actions.${index}.message_sender_assignee`} />
        </div>
        <div className="col-12 col-md-6">
          <LabelName required={true} htmlFor="recipient_list">
            To
          </LabelName>
          <Controller
            name={`actions[${index}].recipient_list`}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field: { name, value, onChange, ref } }) => (
              <MultiSelect
                name={name}
                value={value ?? []}
                options={
                  formatUserDropdown({
                    users: [],
                    hideInactive: true,
                    includeContacts: false,
                    includesUserGroups: false,
                    includesTeamMembers: false,
                    includePrimarySecondary: true,
                    isLead: isForLeadAutomation,
                    includeLawftIs: true,
                    lawftIDs: lawftIdsOption,
                  }) || []
                }
                placeholder="Please Select Recipients"
                className="w-100 p-dropdown p-component p-inputwrapper"
                filter
                onChange={(e) => onChange(e.value)}
                optionValue="value"
                display="chip"
                optionLabel="label"
                optionGroupLabel="groupLabel"
                optionGroupChildren="items"
                optionDisabled={(opt) => ccSelectedKeys.has(opt?.value)}
                itemTemplate={assignToItemTemplate}
                inputRef={ref}
              />
            )}
          />
          <RequiredValidation errors={errors} field={`actions.${index}.recipient_list`} />
        </div>

        <div className="col-12 col-md-6">
          <LabelName htmlFor="cc_list">Cc</LabelName>
          <Controller
            name={`actions[${index}].cc_list`}
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <MultiSelect
                name={name}
                value={value ?? []}
                options={
                  formatUserDropdown({
                    users: [],
                    hideInactive: true,
                    includeContacts: false,
                    includesUserGroups: false,
                    includesTeamMembers: false,
                    includePrimarySecondary: true,
                    isLead: isForLeadAutomation,
                    includeLawftIs: true,
                    lawftIDs: lawftIdsOption,
                  }) || []
                }
                placeholder="Please Select Cc Recipients"
                className="w-100 p-dropdown p-component p-inputwrapper"
                filter
                onChange={(e) => onChange(e.value)}
                optionValue="value"
                display="chip"
                optionLabel="label"
                optionGroupLabel="groupLabel"
                optionGroupChildren="items"
                optionDisabled={(opt) => toSelectedKeys.has(opt?.value)}
                itemTemplate={assignToItemTemplate}
                inputRef={ref}
              />
            )}
          />
        </div>

        <div className="col-12 col-md-6">
          <LabelName required={true} htmlFor="subject">
            Subject
          </LabelName>
          <Controller
            name={`actions[${index}].subject`}
            control={control}
            rules={{ required: 'Required' }}
            defaultValue=""
            render={({ field }) => (
              <TextSnippetWrapper
                type="text"
                id={field.name}
                {...field}
                placeholder="Subject"
                className={'w-100 input-shadow'}
                autoComplete="off"
              />
            )}
          />
          <RequiredValidation errors={errors} field={`actions.${index}.subject`} />
        </div>
        <div className="col-12">
          {isForLeadAutomation && (
            <div className="d-flex files-note">
              <i className="pi pi-info-circle me-1"></i>
              If 'On Behalf Of' contact is not available, 'Client' will be used automatically, even if not selected.
            </div>
          )}
        </div>

        <div className="col-12 create-msg-popup w-100">
          <LabelName htmlFor="email_content">Body</LabelName>
          <Controller
            control={control}
            name={`actions[${index}].email_content`}
            render={({ field: { onChange, value } }) => (
              <TextSnippetWrapper
                type="editor"
                value={sanitizeHtmlColorContent(value)}
                onChange={(e) => onChange(e)}
                className="email-quill-editor shadow-small"
                autoComplete="off"
              />
            )}
          />
          <RequiredValidation errors={errors} field={`actions.${index}.email_content`} />
        </div>

        <div className="col-12">
          <LabelName required={false} htmlFor="attachments">
            Attachments
          </LabelName>
          <AttachmentInput index={index} />
        </div>
      </div>
    </>
  );
}
